import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, User, ShieldCheck } from "lucide-react";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";
import { blogPosts, popularCountries } from "../mock";

export default function Blog() {
  const [featured, ...rest] = blogPosts;
  return (
    <>
      <Seo
        title="ShipMeds Blog | Ayurvedic & Homeopathy Medicine Shipping Guides"
        description="Expert guides on couriering branded & unbranded Ayurvedic and Homeopathy medicines abroad from India, with 100% destination customs clearance guarantee tips."
        path="/blog"
      />
      <PageHero title="ShipMeds Blog" subtitle="Guides on shipping branded & unbranded Ayurvedic and Homeopathy medicines worldwide - with guaranteed customs clearance." crumb="Blog" />

      <section className="bg-teal-600">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 text-white text-center">
          <ShieldCheck className="w-6 h-6 shrink-0" />
          <p className="text-sm sm:text-base font-medium">India's first medicine courier with a <span className="font-bold">100% customs clearance guarantee</span> on Ayurvedic &amp; Homeopathy medicines.</p>
          <Link to="/blog/100-percent-customs-clearance-guarantee-ayurvedic-homeopathy" className="shrink-0 bg-white text-teal-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-teal-50 transition-colors">Learn how</Link>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <Link to={`/blog/${featured.slug}`} className="group grid lg:grid-cols-2 gap-8 mb-14 bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 card-shadow">
            <div className="h-64 lg:h-full overflow-hidden">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-8 flex flex-col justify-center">
              <span className="inline-block w-fit text-teal-600 font-bold uppercase tracking-widest text-xs bg-teal-100/60 px-3 py-1 rounded-full mb-4">Featured</span>
              <h2 className="text-2xl lg:text-3xl font-extrabold text-slate-900 leading-tight mb-3 group-hover:text-teal-600 transition-colors">{featured.title}</h2>
              <p className="text-slate-600 leading-relaxed mb-5">{featured.excerpt}</p>
              <div className="flex items-center gap-4 text-sm text-slate-400 mb-5">
                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{featured.date}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{featured.readTime}</span>
              </div>
              <span className="inline-flex items-center gap-2 text-teal-600 font-semibold">Read Article <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
            </div>
          </Link>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 card-shadow hover:-translate-y-1 transition-transform flex flex-col">
                <div className="h-48 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  {(p.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(p.tags || []).slice(0, 2).map((t) => (
                        <span key={t} className="text-[10px] font-bold uppercase tracking-wide text-teal-700 bg-teal-50 px-2 py-1 rounded-full">{t}</span>
                      ))}
                    </div>
                  )}
                  <h3 className="font-bold text-slate-900 leading-snug mb-2 group-hover:text-teal-600 transition-colors">{p.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400 mt-4">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{p.date}</span>
                    <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{p.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Interlink to country pages */}
          <div className="mt-16 bg-slate-50 border border-slate-100 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-slate-900 mb-1">Popular Shipping Destinations</h3>
            <p className="text-slate-500 text-sm mb-6">Explore Ayurvedic &amp; Homeopathy medicine courier charges by country.</p>
            <div className="flex flex-wrap gap-3">
              {popularCountries.slice(0, 12).map((c) => (
                <Link key={c.slug} to={`/country/${c.slug}`} className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-teal-400 hover:text-teal-600 text-slate-700 text-sm font-medium px-4 py-2 rounded-full transition-colors">
                  <img src={`https://flagcdn.com/w40/${c.code}.png`} alt={c.name} className="w-5 h-3.5 object-cover rounded-sm" /> India to {c.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
